"use client"

// packages/frontend/src/components/ui/reactbits/ClickSparkWrapper.tsx

import React, { type ReactNode } from "react"
import { motion } from "framer-motion"
import { useTheme } from "../../providers/ThemeProvider"
import { cn } from "@/utils/cn"

interface ClickSparkWrapperProps {
  children: ReactNode
  className?: string
  disabled?: boolean
}

/**
 * Wrapper for ReactBits ClickSpark component with theme support
 * Provides sparkle animation on click for child engagement
 */
export function ClickSparkWrapper({
  children,
  className,
  disabled = false,
}: ClickSparkWrapperProps) {
  const { themeType } = useTheme()
  const isChildTheme = themeType === "child"

  // If disabled or not child theme, return children without animation
  if (disabled || !isChildTheme) {
    return <>{children}</>
  }

  // Try to import ReactBits ClickSpark, fallback to custom implementation
  let ClickSpark: any = null
  try {
    // Attempt to import from ReactBits
    const reactbits = require("@appletosolutions/reactbits")
    ClickSpark = reactbits.ClickSpark || reactbits.default?.ClickSpark
  } catch (e) {
    // Fallback to custom implementation
  }

  // Custom sparkle effect using Framer Motion
  const [sparks, setSparks] = React.useState<Array<{ id: number; x: number; y: number }>>([])

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return

    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    // Create sparkle particles
    const newSparks = Array.from({ length: 6 }, (_, i) => ({
      id: Date.now() + i,
      x: x + (Math.random() - 0.5) * 20,
      y: y + (Math.random() - 0.5) * 20,
    }))

    setSparks((prev) => [...prev, ...newSparks])

    // Remove sparks after animation
    setTimeout(() => {
      setSparks((prev) => prev.filter((spark) => !newSparks.find((s) => s.id === spark.id)))
    }, 600)
  }

  // If ReactBits component is available, use it
  if (ClickSpark) {
    return (
      <ClickSpark className={className} disabled={disabled}>
        {children}
      </ClickSpark>
    )
  }

  // Custom implementation with Framer Motion
  return (
    <div className={cn("relative", className)} onClick={handleClick}>
      {children}
      {sparks.map((spark) => (
        <motion.div
          key={spark.id}
          className="absolute pointer-events-none z-50"
          style={{
            left: spark.x,
            top: spark.y,
          }}
          initial={{
            scale: 0,
            opacity: 1,
            x: 0,
            y: 0,
          }}
          animate={{
            scale: [0, 1.2, 0],
            opacity: [1, 1, 0],
            x: (Math.random() - 0.5) * 40,
            y: (Math.random() - 0.5) * 40,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
        >
          <div className="w-2 h-2 bg-linear-to-br from-yellow-400 via-pink-400 to-purple-400 rounded-full shadow-lg" />
        </motion.div>
      ))}
    </div>
  )
}

